import { type JSX, Show } from "solid-js";
import { User } from "lucide-solid";
import { cn, type FrameId } from "@famidex/shared";

/**
 * 🍂 Marco estacional decorativo alrededor del avatar del coleccionista.
 * Traducción del diseño Stitch `a_highly_detailed_circular_decorative_frame…`
 * (variante Autumn) + selección de estación. Anillo de gradiente cálido/frío
 * según la estación con un pequeño acento; SIN assets externos (self-contained,
 * theme-safe: el anillo lo dibuja un gradiente y el hueco usa el bg del tema).
 * Respeta prefers-reduced-motion (no hay animación de movimiento).
 */

interface FrameStyle {
  /** Gradiente cónico del anillo. */
  ring: string;
  /** Emoji de acento (esquina superior). */
  accent: string;
}

// El marco de OTOÑO es el canónico del export (`…marco_de_otono`); se dibuja con
// los tokens cálidos de marca (warning/cherry/cream) para no salirse de Forest
// Nurture. Las otras 3 estaciones son unlockables decorativos deliberadamente
// fuera de la paleta (necesitan azules/rosas fríos que la marca no define); se
// mantienen como gradientes explícitos y aislados a este componente decorativo.
const FRAME_STYLES: Record<FrameId, FrameStyle> = {
  autumn: {
    ring: "conic-gradient(from 210deg, var(--warning), var(--brand-cherry), var(--warning), var(--brand-cream), var(--warning))",
    accent: "🍁",
  },
  spring: {
    ring: "conic-gradient(from 150deg, #ec4899, #f472b6, #a3e635, #34d399, #ec4899)",
    accent: "🌸",
  },
  winter: {
    ring: "conic-gradient(from 200deg, #0ea5e9, #38bdf8, #e0f2fe, #6366f1, #0ea5e9)",
    accent: "❄️",
  },
  summer: {
    ring: "conic-gradient(from 160deg, #f59e0b, #fde047, #14b8a6, #22d3ee, #f59e0b)",
    accent: "☀️",
  },
};

interface SeasonalFrameProps {
  frameId?: string | null;
  imageUrl?: string | null;
  alt?: string;
  /** Diameter in px (default 112 = h-28). */
  size?: number;
  class?: string;
  /** Optional badge rendered at the bottom (e.g. rank title). */
  badge?: JSX.Element;
}

export function SeasonalFrame(props: SeasonalFrameProps) {
  const style = (): FrameStyle | null => {
    switch (props.frameId) {
      case "autumn":
      case "spring":
      case "winter":
      case "summer": {
        return FRAME_STYLES[props.frameId];
      }
      default: {
        return null;
      }
    }
  };
  const size = () => props.size ?? 112;
  // Ring thickness scales with the avatar; framed avatars get a thicker ring.
  const ring = () => (style() ? Math.max(5, Math.round(size() * 0.07)) : 3);

  return (
    <div
      class={cn("relative shrink-0", props.class)}
      style={{ width: `${size()}px`, height: `${size()}px` }}
      data-testid="collector-avatar"
      data-frame={props.frameId ?? "none"}
    >
      {/* Anillo (gradiente estacional) o borde simple si no hay marco. */}
      <div
        class="absolute inset-0 rounded-full"
        style={
          style()
            ? { background: style()!.ring, padding: `${ring()}px` }
            : { background: "var(--border)", padding: `${ring()}px` }
        }
      >
        <div class="h-full w-full overflow-hidden rounded-full bg-muted">
          <Show
            when={props.imageUrl}
            fallback={
              <div class="grid h-full w-full place-items-center text-muted-foreground/60">
                <User style={{ width: "45%", height: "45%" }} />
              </div>
            }
          >
            <img
              src={props.imageUrl ?? ""}
              alt={props.alt ?? ""}
              class="h-full w-full object-cover"
              loading="lazy"
            />
          </Show>
        </div>
      </div>

      {/* Acento estacional (esquina superior). */}
      <Show when={style()}>
        <span
          aria-hidden="true"
          class="absolute -top-1 -right-1 grid place-items-center rounded-full bg-card shadow-md ring-1 ring-border/50"
          style={{
            width: `${Math.round(size() * 0.26)}px`,
            height: `${Math.round(size() * 0.26)}px`,
            "font-size": `${Math.round(size() * 0.15)}px`,
          }}
        >
          {style()!.accent}
        </span>
      </Show>

      {/* Badge inferior (título / rango). */}
      <Show when={props.badge}>
        <div class="absolute -bottom-2 left-1/2 -translate-x-1/2">{props.badge}</div>
      </Show>
    </div>
  );
}
